import type { PropsWithChildren, ReactNode } from "react";
import { useState } from "react";
import { Box, LinearProgress } from "@mui/material";
import type { ChannelListMessengerProps } from "stream-chat-react";
import { useTranslationContext } from "stream-chat-react";

/**
 * The `List` of our ChannelList. Replaces the SDK's ChannelListMessenger for one reason: on every
 * filter change (search term, category tab, rejected toggle) the SDK empties its channels and the
 * default list swaps to the loading indicator for the whole round trip, so the user sees their
 * rows vanish into a skeleton and come back. This keeps the last rendered rows on screen under a
 * thin progress bar until the new rows replace them in place. The skeleton remains for a reload
 * that has nothing to keep: the first load, or leaving a tab that was empty.
 *
 * The wrapper markup and class names mirror the SDK's messenger (`str-chat__channel-list-messenger*`)
 * so its styles and the paginator keep working; a major SDK upgrade may rename them.
 */
export const ChatChannelListContainer = ({
  children,
  error,
  loading,
  loadedChannels,
  LoadingErrorIndicator,
  LoadingIndicator,
}: PropsWithChildren<ChannelListMessengerProps>) => {
  const { t } = useTranslationContext("ChatChannelListContainer");

  // The rows worth keeping are the children of the last non-loading render. Remembered the way
  // React prescribes for "something from the previous render": state set conditionally DURING
  // render, which re-runs this component once with the same props (same children reference, so
  // the guard stops it) before anything commits — no ref written in render, no second commit.
  // `loadedChannels` arrives because ChannelList is given `sendChannelsToList`; it tells an empty
  // state (not worth keeping) apart from real rows.
  const [heldRows, setHeldRows] = useState<ReactNode>(null);
  if (!loading) {
    const rows = loadedChannels?.length ? children : null;
    if (rows !== heldRows) {
      setHeldRows(rows);
    }
  }

  if (error) {
    return LoadingErrorIndicator ? (
      <LoadingErrorIndicator error={error} />
    ) : null;
  }

  const isHoldingPreviousRows = loading && heldRows !== null;

  if (loading && !isHoldingPreviousRows) {
    return LoadingIndicator ? <LoadingIndicator /> : null;
  }

  return (
    <Box
      className="str-chat__channel-list-messenger str-chat__channel-list-messenger-react"
      position="relative"
    >
      {isHoldingPreviousRows && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }}
        />
      )}
      <div
        aria-label={t("aria/Channel list")}
        aria-busy={isHoldingPreviousRows}
        className="str-chat__channel-list-messenger__main str-chat__channel-list-messenger-react__main"
        role="listbox"
      >
        {isHoldingPreviousRows ? heldRows : children}
      </div>
    </Box>
  );
};
